import { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, ArrowLeft, User, Baby, Calendar, Filter, Check, ShoppingCart, Accessibility, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import {
  useAdminRegistrationStore,
  type ParentResult,
  type Kid,
  type SessionResult,
} from '../../stores/adminRegistrationStore';
import { useDevis } from '../../lib/orders/useDevis';
import { euros } from '../../lib/orders/devis';
import { produireEtEnvoyerFacture } from '../../lib/orders/gateway.supabase';
import { resultatInscriptionAdmin } from '../../lib/orders/resultatInscriptionAdmin';
import RecapitulatifInscription from '../../components/admin/inscription/RecapitulatifInscription';
import { remainingPlaces } from '../../lib/places';

/*
  Une semaine retenue. Elle ne porte ni prix ni type de tarif : les deux
  viennent de `quote_order`, comme pour le parent. L'ancienne version les
  recalculait ici a chaque bascule de la case « tarif reduit », avec un
  `calculatedReducedPrice` qui pouvait etre nul — la semaine restait alors
  silencieusement au tarif normal.
*/
interface SelectedSession {
  session: SessionResult;
}

function calculateAge(dateNaissance: string): string {
  const birth = new Date(dateNaissance);
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    years--;
  }
  return `${years} ans`;
}

const AdminInscriptionsPage = () => {
  const store = useAdminRegistrationStore();

  const [step, setStep] = useState<'search' | 'kids' | 'sessions' | 'confirm'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedParent, setSelectedParent] = useState<ParentResult | null>(null);
  const [selectedKid, setSelectedKid] = useState<Kid | null>(null);

  const [centers, setCenters] = useState<{ id: string; name: string }[]>([]);
  const [filterCenter, setFilterCenter] = useState('');
  const [filterPeriode, setFilterPeriode] = useState('');
  const [selectedSessions, setSelectedSessions] = useState<SelectedSession[]>([]);

  const [reducedDeclaration, setReducedDeclaration] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    const loadCenters = async () => {
      const { data } = await supabase
        .from('centers')
        .select('id, name')
        .eq('active', true)
        .eq('archived', false)
        .order('name');
      if (data) setCenters(data);
    };
    loadCenters();
  }, []);

  // --- Step 1: Search ---
  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const term = searchTerm.trim();
    if (!term || term.length < 2) {
      toast.error('Veuillez saisir au moins 2 caractères');
      return;
    }
    setHasSearched(true);
    try {
      await store.searchParents(term);
    } catch {
      toast.error('Erreur lors de la recherche');
    }
  };

  // --- Step 2: Select parent, load kids ---
  const handleSelectParent = async (parent: ParentResult) => {
    setSelectedParent(parent);
    setStep('kids');
    try {
      await store.fetchKidsForParent(parent.id);
    } catch {
      toast.error('Erreur lors du chargement des enfants');
    }
  };

  // --- Step 3: Select kid, load sessions ---
  const handleSelectKid = async (kid: Kid) => {
    setSelectedKid(kid);
    setStep('sessions');
    setSelectedSessions([]);
    try {
      await store.fetchAvailableSessions(kid);
    } catch {
      toast.error('Erreur lors du chargement des sessions');
    }
  };

  const handleFilterChange = async (centerId: string, periode: string) => {
    setFilterCenter(centerId);
    setFilterPeriode(periode);
    if (selectedKid) {
      try {
        await store.fetchAvailableSessions(selectedKid, {
          centerId: centerId || undefined,
          periode: periode || undefined,
        });
      } catch {
        toast.error('Erreur lors du chargement des sessions');
      }
    }
  };

  const toggleSession = (session: SessionResult) => {
    const exists = selectedSessions.find(s => s.session.id === session.id);
    if (exists) {
      setSelectedSessions(prev => prev.filter(s => s.session.id !== session.id));
    } else {
      setSelectedSessions(prev => [...prev, { session }]);
    }
  };

  // --- Step 4: Confirm ---
  const handleGoToConfirm = () => {
    if (selectedSessions.length === 0) {
      toast.error('Veuillez sélectionner au moins une session');
      return;
    }
    setStep('confirm');
  };

  /*
    Cocher la case ne touche a aucun prix : le devis est redemande a la base
    avec le drapeau, et c'est elle qui repond. Il n'y a plus de regle de tarif
    dans cet ecran.
  */
  const handleToggleReduced = () => setReducedDeclaration(!reducedDeclaration);

  const handleConfirm = async () => {
    if (!selectedParent || !selectedKid || selectedSessions.length === 0) return;

    try {
      const { invoiceNumber, commande } = await store.createAdminRegistration(
        selectedParent.id, items, reducedDeclaration, codes,
      );

      /*
        Le document et le courriel, comme du cote parent. Cet ecran ne les
        produisait pas : la facture et les inscriptions existaient, mais
        aucun PDF ne sortait et la famille ne recevait rien.

        On ATTEND l'envoi, contrairement au tunnel parent : ici c'est un poste
        de travail, et savoir que le courriel est parti fait partie du geste.
        Un echec n'annule rien — l'enfant est inscrit et la facture existe —
        mais il se DIT, plutot que d'annoncer un succes qui n'en est pas un.
      */
      const envoi = await produireEtEnvoyerFacture(invoiceNumber, selectedParent.email);

      setResultModal(resultatInscriptionAdmin(invoiceNumber, commande, envoi, selectedParent.email));
      // Reset state after success
      setStep('search');
      setSelectedParent(null);
      setSelectedKid(null);
      setSelectedSessions([]);
      setReducedDeclaration(false);
      setCodes([]);
      setSearchTerm('');
      setHasSearched(false);
      store.reset();
    } catch (err: any) {
      setResultModal({
        type: 'error',
        message: err.message || 'Une erreur est survenue lors de la création de l\'inscription.'
      });
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('sessions');
    } else if (step === 'sessions') {
      setStep('kids');
      setSelectedKid(null);
      setSelectedSessions([]);
    } else if (step === 'kids') {
      setStep('search');
      setSelectedParent(null);
    }
  };

  /*
    Les lignes de la commande. Elles ne portent que des identifiants ; le reste
    ne sert qu'a l'affichage du recapitulatif.
  */
  const items = useMemo(() => selectedKid ? selectedSessions.map(s => ({
    id: `${s.session.id}-${selectedKid.id}`,
    activity_id: s.session.id,
    kid_id: selectedKid.id,
    kidName: `${selectedKid.prenom} ${selectedKid.nom}`,
    activityName: s.session.stage.title,
    activityCategory: s.session.center.name,
    dateRange: `${formatDate(s.session.start_date)} - ${formatDate(s.session.end_date)}`,
    imageUrl: s.session.stage.image_url || undefined,
  })) : [], [selectedSessions, selectedKid]);

  const lignes = useMemo(
    () => items.map(i => ({ session_id: i.activity_id, kid_id: i.kid_id })),
    [items],
  );

  /* Le devis, demande pour LA FAMILLE : `quote_order` autorise un admin. */
  const { devis, chargement: devisEnCours, erreur: erreurDevis } = useDevis(
    selectedParent?.id, lignes, reducedDeclaration, codes,
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Inscription admin</h1>
        <p className="text-gray-600 mt-1">Inscrire un enfant dans une activité au nom d'un parent</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        <StepIndicator number={1} label="Parent" active={step === 'search'} completed={step !== 'search'} />
        <div className="h-px flex-1 bg-gray-200" />
        <StepIndicator number={2} label="Enfant" active={step === 'kids'} completed={step === 'sessions' || step === 'confirm'} />
        <div className="h-px flex-1 bg-gray-200" />
        <StepIndicator number={3} label="Session" active={step === 'sessions'} completed={step === 'confirm'} />
        <div className="h-px flex-1 bg-gray-200" />
        <StepIndicator number={4} label="Confirmation" active={step === 'confirm'} completed={false} />
      </div>

      {/* Step 1: Parent Search */}
      {step === 'search' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-teal-600" />
              Rechercher un parent
            </h2>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par email, nom, prénom ou registre national..."
                  className="form-input pl-10 w-full"
                  autoFocus
                />
              </div>
              <button type="submit" disabled={store.isSearching} className="btn-primary flex items-center gap-2 px-5">
                {store.isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Rechercher
              </button>
            </form>
          </div>

          {hasSearched && (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {store.parents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Aucun parent trouvé pour "{searchTerm}"
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prénom</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N. National</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {store.parents.map((parent) => (
                        <tr key={parent.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{parent.nom || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{parent.prenom || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{parent.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{parent.telephone || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{parent.nnational || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleSelectParent(parent)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                            >
                              Sélectionner
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Kids */}
      {step === 'kids' && selectedParent && (
        <div className="space-y-6">
          <ParentSummary parent={selectedParent} onBack={handleBack} />
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Baby className="h-5 w-5 text-teal-600" />
              Enfants liés au profil
            </h2>
            {store.isLoadingKids ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
              </div>
            ) : store.kids.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Aucun enfant trouvé pour ce parent</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {store.kids.map((kid) => (
                  <button
                    key={kid.id}
                    onClick={() => handleSelectKid(kid)}
                    className="text-left p-4 rounded-lg border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors">
                          {kid.prenom} {kid.nom}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 text-sm text-gray-500">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{new Date(kid.date_naissance).toLocaleDateString('fr-FR')}</span>
                          <span className="text-gray-300">|</span>
                          <span className="font-medium text-gray-700">{calculateAge(kid.date_naissance)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {kid.inclusion?.has_needs && (
                          <div className="relative group/tooltip">
                            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                              <Accessibility className="h-4 w-4" />
                            </div>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                              Besoins spécifiques / Handicap
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                            </div>
                          </div>
                        )}
                        <div className="shrink-0 w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 group-hover:bg-teal-200 transition-colors">
                          <span className="text-xs font-bold">{kid.prenom.charAt(0)}{kid.nom.charAt(0)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Sessions */}
      {step === 'sessions' && selectedParent && selectedKid && (
        <div className="space-y-6">
          <ParentSummary parent={selectedParent} onBack={handleBack} kidName={`${selectedKid.prenom} ${selectedKid.nom}`} />

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Filter className="h-5 w-5 text-teal-600" />
              Filtres
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
                <select
                  value={filterCenter}
                  onChange={(e) => handleFilterChange(e.target.value, filterPeriode)}
                  className="form-input w-full"
                >
                  <option value="">Tous les centres</option>
                  {centers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
                <select
                  value={filterPeriode}
                  onChange={(e) => handleFilterChange(filterCenter, e.target.value)}
                  className="form-input w-full"
                >
                  <option value="">Toutes les périodes</option>
                  {store.periodes.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Session list */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {store.isLoadingSessions ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
              </div>
            ) : store.sessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Aucune session disponible pour cet enfant avec les filtres actuels
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Places restantes / capacité</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {store.sessions.map((session) => {
                      const isSelected = selectedSessions.some(s => s.session.id === session.id);
                      const spotsLeft = remainingPlaces(session);
                      const isFull = spotsLeft <= 0;
                      const isDisabled = isFull || session.alreadyRegistered;

                      return (
                        <tr
                          key={session.id}
                          onClick={() => !isDisabled && toggleSession(session)}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-teal-50 hover:bg-teal-100'
                              : isDisabled
                                ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                                : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-teal-500 border-teal-500' : isDisabled ? 'border-gray-200 bg-gray-100' : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{session.stage.title}</div>
                            <div className="text-xs text-gray-500">{session.stage.age_min}-{session.stage.age_max} ans</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{session.center.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(session.start_date)} - {formatDate(session.end_date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {session.periode}
                            {session.semaine && <span className="text-xs text-gray-400 ml-1">S{session.semaine}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {session.alreadyRegistered ? (
                              <span className="text-red-600 font-medium">Déjà inscrit</span>
                            ) : isFull ? (
                              <span className="text-red-600 font-medium">Complet</span>
                            ) : (
                              <span className={spotsLeft <= 3 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                                {spotsLeft}/{session.capacity}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm font-semibold text-gray-900">{session.calculatedPrice.toFixed(2)}€</div>
                            {session.isLocal && <div className="text-xs text-teal-600">Tarif local</div>}
                            {session.calculatedReducedPrice !== null
                              && session.calculatedReducedPrice !== session.calculatedPrice && (
                              <div className="text-xs text-gray-400">
                                Réduit: {session.calculatedReducedPrice.toFixed(2)}€
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Floating action bar */}
          {selectedSessions.length > 0 && (
            <div className="sticky bottom-4 bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShoppingCart className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium text-gray-700">
                  {selectedSessions.length} session{selectedSessions.length > 1 ? 's' : ''} sélectionnée{selectedSessions.length > 1 ? 's' : ''}
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {devis ? euros(devis.amount_cents) : (devisEnCours ? '…' : '—')}
                </span>
              </div>
              <button onClick={handleGoToConfirm} className="btn-primary flex items-center gap-2">
                Continuer
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Confirmation */}
      {step === 'confirm' && selectedParent && selectedKid && (
        <div className="space-y-6">
          <ParentSummary parent={selectedParent} onBack={handleBack} kidName={`${selectedKid.prenom} ${selectedKid.nom}`} />

          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Récapitulatif de l'inscription</h2>

            <RecapitulatifInscription
              kidId={selectedKid.id}
              semaines={selectedSessions.map((s) => ({
                sessionId: s.session.id,
                stage: s.session.stage.title,
                centre: s.session.center.name,
                dates: `${formatDate(s.session.start_date)} - ${formatDate(s.session.end_date)}`,
              }))}
              devis={devis}
              devisEnCours={devisEnCours}
              erreurDevis={erreurDevis}
              reduit={reducedDeclaration}
              onBasculerReduit={handleToggleReduced}
              codes={codes}
              onCodes={setCodes}
              enCours={store.isSubmitting}
              onConfirmer={handleConfirm}
              onRetour={handleBack}
            />
          </div>
        </div>
      )}

      {/* Result Modal */}
      {resultModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setResultModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <button
              onClick={() => setResultModal(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              {resultModal.type === 'success' ? (
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-7 w-7 text-red-600" />
                </div>
              )}
              <h3 className={`text-lg font-semibold mb-2 ${
                resultModal.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {resultModal.type === 'success' ? 'Inscription réussie' : 'Erreur'}
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-line mb-6">
                {resultModal.message}
              </p>
              <button
                onClick={() => setResultModal(null)}
                className={`px-6 py-2.5 rounded-lg font-medium text-white transition-colors ${
                  resultModal.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Helper Components ---

function ParentSummary({ parent, onBack, kidName }: { parent: ParentResult; onBack: () => void; kidName?: string }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <User className="h-5 w-5 text-teal-600" />
            {parent.prenom} {parent.nom}
            {kidName && (
              <>
                <span className="text-gray-300 mx-1">/</span>
                <Baby className="h-4 w-4 text-teal-500" />
                <span className="text-base font-medium text-gray-700">{kidName}</span>
              </>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{parent.email}</p>
        </div>
        <div className="text-right text-sm text-gray-500">
          {parent.telephone && <p>{parent.telephone}</p>}
          {parent.nnational && <p className="font-mono text-xs mt-0.5">{parent.nnational}</p>}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ number, label, active, completed }: {
  number: number; label: string; active: boolean; completed: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
        completed ? 'bg-teal-500 text-white' : active ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500' : 'bg-gray-100 text-gray-400'
      }`}>
        {completed ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : number}
      </div>
      <span className={`text-sm font-medium ${active ? 'text-teal-700' : completed ? 'text-teal-600' : 'text-gray-400'}`}>
        {label}
      </span>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default AdminInscriptionsPage;